import { useState, useRef, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Copy, Megaphone, Play, Pause, Download, Volume2, Eye, Pencil } from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Strip markdown formatting to plain text (used for TTS + character count)
const stripMarkdown = (md) =>
  (md || "")
    .replace(/```[\s\S]*?```/g, "") // fenced code
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/\*\*([^*]+)\*\*/g, "$1") // bold
    .replace(/\*([^*]+)\*/g, "$1") // italic
    .replace(/^#{1,6}\s+/gm, "") // headings
    .replace(/^\s*[-*+]\s+/gm, "") // bullets
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // links
    .replace(/\n{3,}/g, "\n\n") // collapse extra blank lines
    .trim();

// Extract ONLY the "Announcement Script" section for TTS — drops the section
// heading and everything from "Delivery Notes" onwards. Falls back to full
// stripped text if the expected heading isn't found.
const extractAnnouncementForTts = (md) => {
  if (!md) return "";
  const scriptHeading = /^\s*#{1,6}\s*Announcement\s+Script\s*$/im;
  const nextSectionHeading = /^\s*#{1,6}\s+\S/m;
  const start = md.search(scriptHeading);
  if (start === -1) return stripMarkdown(md);
  // Move past the heading line
  const afterHeadingIdx = md.indexOf("\n", start);
  const body = afterHeadingIdx === -1 ? "" : md.slice(afterHeadingIdx + 1);
  // Find next heading to bound the section
  const nextMatch = body.match(nextSectionHeading);
  const section = nextMatch ? body.slice(0, nextMatch.index) : body;
  return stripMarkdown(section);
};

// Markdown renderers — declared outside component to keep referential stability
const SCRIPT_MARKDOWN_COMPONENTS = {
  h1: (props) => (
    <h2 className="text-base font-bold uppercase tracking-wider mt-3" {...props} />
  ),
  h2: (props) => (
    <h3
      className="text-sm font-bold uppercase tracking-wider mt-4 pt-3 border-t border-border/40 first:border-t-0 first:mt-0 first:pt-0"
      {...props}
    />
  ),
  p: (props) => (
    <p className="text-lg leading-relaxed" {...props} />
  ),
  ul: (props) => (
    <ul className="space-y-1 text-sm ml-1 list-none" {...props} />
  ),
  li: ({ children, ...props }) => (
    <li
      className="pl-5 relative before:content-['▸'] before:absolute before:left-0 before:text-foreground/50"
      {...props}
    >
      {children}
    </li>
  ),
  strong: (props) => <strong className="font-bold text-[#E07A5F]" {...props} />,
};

const emergencyTypes = [
  { value: "medical", label: "Medical Emergency", example: "A guest has collapsed in the lobby" },
  { value: "fire", label: "Fire/Evacuation", example: "Fire alarm has been triggered" },
  { value: "security", label: "Security Threat", example: "Suspicious person reported near entrance" },
  { value: "weather", label: "Severe Weather", example: "Tornado warning issued for the area" },
  { value: "power", label: "Power Outage", example: "Electricity has gone out in the main hall" },
  { value: "crowd", label: "Crowd Control", example: "Overcrowding at the main entrance" },
  { value: "vip", label: "VIP Issue", example: "VIP guest needs to leave discreetly" },
  { value: "technical", label: "Technical Failure", example: "AV system has completely failed" },
  { value: "custom", label: "Custom Situation", example: "Describe your specific situation" },
];

// OpenAI TTS voices — ordered by suitability for emergency announcements
const ttsVoices = [
  { value: "nova", label: "Nova — Calm & Reassuring (recommended)" },
  { value: "onyx", label: "Onyx — Authoritative Male" },
  { value: "shimmer", label: "Shimmer — Warm Female" },
  { value: "alloy", label: "Alloy — Neutral" },
  { value: "echo", label: "Echo — Crisp Male" },
  { value: "fable", label: "Fable — Expressive UK" },
  { value: "sage", label: "Sage — Steady" },
  { value: "ash", label: "Ash — Grounded" },
  { value: "coral", label: "Coral — Bright" },
];

export const EmergencyScript = () => {
  const [emergencyType, setEmergencyType] = useState("medical");
  const [situation, setSituation] = useState("");
  const [details, setDetails] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Audio state
  const [voice, setVoice] = useState("nova");
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const selectedType = emergencyTypes.find(t => t.value === emergencyType);

  // Revoke object URL on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const generateScript = async () => {
    const situationText = situation || selectedType?.example || emergencyType;

    if (!situationText) {
      toast.error("Please describe the situation");
      return;
    }

    setLoading(true);
    setResult(null);
    // Reset audio + exit edit mode ONLY on a fresh generation (not on every
    // keystroke inside the edit textarea, which also calls setResult).
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
      setIsPlaying(false);
    }
    setEditMode(false);
    try {
      const response = await axios.post(`${API}/ai/emergency-script`, {
        situation: situationText,
        details: details || undefined
      });
      setResult(response.data.result);
      toast.success("Emergency script generated");
    } catch (error) {
      toast.error("Failed to generate script. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyScript = () => {
    // Copy plain text so it's easy to paste into other tools
    navigator.clipboard.writeText(stripMarkdown(result));
    toast.success("Script copied to clipboard");
  };

  const useExample = () => {
    if (selectedType) {
      setSituation(selectedType.example);
    }
  };

  // --- TTS audio handlers ---
  const base64ToBlob = (b64, mime = "audio/mpeg") => {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  };

  const generateAudio = async () => {
    if (!result) return;
    setAudioLoading(true);
    try {
      // Send only the "Announcement Script" section to TTS — drops the
      // "Announcement Script" heading itself and the "Delivery Notes" section.
      const spokenText = extractAnnouncementForTts(result);
      const response = await axios.post(`${API}/ai/tts`, {
        text: spokenText,
        voice,
      });
      const blob = base64ToBlob(response.data.audio_base64);
      const url = URL.createObjectURL(blob);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(url);
      // Auto-play once ready
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.load();
          audioRef.current.play().catch(() => {
            /* user gesture issue is unlikely since this IS a click */
          });
        }
      }, 50);
      toast.success("Audio ready — playing now");
    } catch (error) {
      toast.error("Failed to generate audio. Please try again.");
      console.error(error);
    } finally {
      setAudioLoading(false);
    }
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const downloadAudio = () => {
    if (!audioUrl) return;
    const a = document.createElement("a");
    a.href = audioUrl;
    a.download = `emergency-script-${emergencyType}-${voice}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Layout
      title="Emergency Script Generator"
      subtitle="AI-generated calm announcements for event emergencies"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Warning Card */}
        <Card className="tactical-card border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-400">Important</p>
                <p className="text-sm text-muted-foreground">
                  These scripts are AI-generated guides. Always follow your venue&apos;s official emergency
                  procedures and coordinate with security/emergency services first.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Input Card */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Megaphone className="h-5 w-5 text-purple-400" />
              Situation Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="label-tactical">Emergency Type</Label>
              <Select value={emergencyType} onValueChange={setEmergencyType}>
                <SelectTrigger className="tactical-input" data-testid="emergency-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {emergencyTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="label-tactical">Describe the Situation</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={useExample}
                  data-testid="use-example-button"
                >
                  Use Example
                </Button>
              </div>
              <Input
                placeholder={selectedType?.example}
                value={situation}
                onChange={(e) => setSituation(e.target.value)}
                className="tactical-input"
                data-testid="situation-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="label-tactical">Additional Details (Optional)</Label>
              <Textarea
                placeholder="Location in venue, number of people affected, any specific instructions needed..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                className="tactical-input min-h-[100px]"
                data-testid="details-input"
              />
            </div>

            <Button
              onClick={generateScript}
              className="tactical-btn w-full bg-purple-600 hover:bg-purple-700"
              disabled={loading}
              data-testid="generate-script-button"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Generating Script...
                </>
              ) : (
                <>
                  <Megaphone className="h-5 w-5 mr-2" />
                  Generate Announcement Script
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Result Card */}
        {result && (
          <Card className="tactical-card border-purple-500/30">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="flex items-center gap-2 text-lg uppercase text-purple-400">
                  <Megaphone className="h-5 w-5" />
                  Announcement Script
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditMode((v) => !v)}
                    data-testid="toggle-edit-button"
                  >
                    {editMode ? (
                      <>
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </>
                    ) : (
                      <>
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyScript}
                    data-testid="copy-script-button"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="bg-secondary/30 p-4 rounded-sm space-y-2"
                data-testid="script-result"
              >
                <div className="flex items-center justify-between">
                  <p className="label-tactical text-purple-400">
                    {editMode ? "EDITING SCRIPT (MARKDOWN)" : "READ ALOUD"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {stripMarkdown(result).length} chars spoken
                  </p>
                </div>

                {editMode ? (
                  <Textarea
                    value={result}
                    onChange={(e) => setResult(e.target.value)}
                    className="tactical-input min-h-[220px] text-base leading-relaxed whitespace-pre-wrap font-mono"
                    maxLength={4096}
                    data-testid="script-edit-textarea"
                  />
                ) : (
                  <div
                    className="min-h-[180px] space-y-3"
                    data-testid="script-preview"
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={SCRIPT_MARKDOWN_COMPONENTS}
                    >
                      {result}
                    </ReactMarkdown>
                  </div>
                )}

                <p className="text-xs text-muted-foreground italic">
                  {editMode
                    ? "Use markdown: **bold** for emphasis, blank lines for pauses. Switch to Preview to see how it will read."
                    : "Bold phrases indicate stress points when reading aloud. Toggle Edit to refine the wording."}
                </p>
              </div>

              {/* Audio Playback Controls */}
              <div className="border border-purple-500/20 bg-purple-500/5 rounded-sm p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-purple-400" />
                  <p className="label-tactical text-purple-400 text-sm">PLAY AS AUDIO</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Voice</Label>
                    <Select value={voice} onValueChange={setVoice}>
                      <SelectTrigger className="tactical-input" data-testid="tts-voice-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ttsVoices.map((v) => (
                          <SelectItem key={v.value} value={v.value}>
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={generateAudio}
                    disabled={audioLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="generate-audio-button"
                  >
                    {audioLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Volume2 className="h-4 w-4 mr-2" />
                        {audioUrl ? "Regenerate" : "Generate Audio"}
                      </>
                    )}
                  </Button>
                </div>

                {audioUrl && (
                  <div className="space-y-3 pt-2">
                    <audio
                      ref={audioRef}
                      src={audioUrl}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onEnded={() => setIsPlaying(false)}
                      controls
                      className="w-full"
                      data-testid="tts-audio-player"
                    />
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={togglePlayback}
                        data-testid="tts-play-pause-button"
                      >
                        {isPlaying ? (
                          <>
                            <Pause className="h-4 w-4 mr-1" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-1" />
                            Play
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={downloadAudio}
                        data-testid="tts-download-button"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download MP3
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Speak clearly and calmly • Pause between sentences • Repeat if necessary
              </p>
            </CardContent>
          </Card>
        )}

        {/* Quick Templates */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="text-lg uppercase">Quick Scenarios</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {emergencyTypes.slice(0, 6).map((type) => (
                <div
                  key={type.value}
                  className={`p-3 rounded-sm border transition-colors cursor-pointer ${
                    emergencyType === type.value
                      ? 'border-purple-500/50 bg-purple-500/10'
                      : 'border-border/50 hover:border-border'
                  }`}
                  onClick={() => {
                    setEmergencyType(type.value);
                    setSituation(type.example);
                  }}
                >
                  <p className={`text-sm font-semibold ${emergencyType === type.value ? 'text-purple-400' : ''}`}>
                    {type.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{type.example}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EmergencyScript;
